import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { SITE, NAV } from "../data/mock";

const MegaPanel = ({ item, onLinkClick }) => {
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[min(960px,90vw)] bg-white border border-[hsl(var(--border))] rounded-2xl shadow-[0_30px_70px_-30px_rgba(60,40,90,0.35)] overflow-hidden">
      <div className="grid grid-cols-12 gap-0">
        {/* Columns */}
        <div className="col-span-12 md:col-span-8 p-7 grid sm:grid-cols-2 gap-7">
          {item.columns?.map((col) => (
            <div key={col.title}>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold mb-3">
                {col.title}
              </div>
              <ul className="space-y-1">
                {col.items.map((sub) => (
                  <li key={sub.label}>
                    <Link
                      to={sub.href}
                      onClick={onLinkClick}
                      className="group block rounded-lg px-3 py-2.5 -mx-3 hover:bg-[hsl(var(--rc-purple-soft))]/60 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-[14px] font-medium text-[hsl(var(--rc-charcoal))] group-hover:text-[hsl(var(--rc-deep))]">
                          {sub.label}
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                      </div>
                      {sub.desc && (
                        <div className="mt-0.5 text-[12.5px] text-[hsl(var(--muted-foreground))] leading-snug">
                          {sub.desc}
                        </div>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Feature card */}
        {item.feature && (
          <Link
            to={item.feature.cta.href}
            onClick={onLinkClick}
            className="col-span-12 md:col-span-4 relative bg-[hsl(var(--rc-deep))] text-white p-7 flex flex-col justify-between min-h-[260px] overflow-hidden group"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-30 group-hover:opacity-40 transition-opacity"
              style={{
                backgroundImage: `url(${item.feature.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-tr from-[hsl(var(--rc-deep))] via-[hsl(var(--rc-deep))]/85 to-transparent"
            />
            <div className="relative">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--rc-gold))] font-semibold">
                Featured
              </div>
              <h4 className="font-serif-display mt-3 text-[22px] font-semibold leading-tight">
                {item.feature.title}
              </h4>
              <p className="mt-2 text-[13px] text-white/80 leading-relaxed">
                {item.feature.desc}
              </p>
            </div>
            <div className="relative inline-flex items-center gap-2 text-[13px] font-medium text-[hsl(var(--rc-gold))]">
              {item.feature.cta.label}
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenIdx(null);
  }, [pathname]);

  const handleEnter = (i) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenIdx(i);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenIdx(null), 120);
  };

  const isActive = (href) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-[hsl(var(--border))] shadow-[0_2px_20px_-12px_rgba(60,40,90,0.18)]"
          : "bg-white/85 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="hidden md:block bg-[hsl(var(--rc-deep))] text-white text-[12px]">
        <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center justify-between">
          <span className="tracking-wide opacity-90">
            A 501(c)(3) Recovery Community Organization · Central Florida
          </span>
          <a
            href={`tel:${SITE.helplineTel}`}
            className="flex items-center gap-2 hover:text-[hsl(var(--rc-gold))] transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            <span className="font-medium">
              Helpline: {SITE.helpline} ({SITE.helplineDigits})
            </span>
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-6">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center gap-3 group">
            <img src={SITE.logoColor} alt="Recovery Connections" className="w-10 h-10 object-contain" />
            <div className="leading-tight">
              <div className="font-serif-display text-[18px] md:text-[19px] font-semibold text-[hsl(var(--rc-deep))]">
                Recovery Connections
              </div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted-foreground))]">
                of Central Florida
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item, i) => {
              const hasMenu = !!item.columns;
              const active = isActive(item.href);
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={hasMenu ? () => handleEnter(i) : undefined}
                  onMouseLeave={hasMenu ? handleLeave : undefined}
                >
                  <Link
                    to={item.href}
                    className={`inline-flex items-center gap-1 px-3.5 h-10 rounded-full text-[14px] font-medium transition-colors ${
                      active
                        ? "text-[hsl(var(--rc-deep))] bg-[hsl(var(--rc-purple-soft))]/60"
                        : "text-[hsl(var(--rc-charcoal))] hover:text-[hsl(var(--rc-deep))] hover:bg-[hsl(var(--secondary))]"
                    }`}
                  >
                    {item.label}
                    {hasMenu && (
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${
                          openIdx === i ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </Link>
                  {hasMenu && openIdx === i && (
                    <MegaPanel item={item} onLinkClick={() => setOpenIdx(null)} />
                  )}
                </div>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${SITE.helplineTel}`}
              className="text-[13px] font-semibold text-[hsl(var(--rc-deep))] hover:text-[hsl(var(--rc-purple))] transition-colors"
            >
              {SITE.helpline}
            </a>
            <Button
              asChild
              className="bg-[hsl(var(--rc-deep))] hover:bg-[hsl(var(--rc-purple))] text-white rounded-full px-5 h-10 shadow-none"
            >
              <Link to="/donate">Donate</Link>
            </Button>
          </div>

          <button
            aria-label="Toggle menu"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-md hover:bg-[hsl(var(--secondary))] transition-colors"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[hsl(var(--border))] bg-white max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col px-5 py-3">
            {NAV.map((item, i) => (
              <div key={item.label} className="border-b border-[hsl(var(--border))] last:border-b-0">
                <div className="flex items-center justify-between">
                  <Link
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex-1 py-3.5 text-[15px] font-medium text-[hsl(var(--rc-charcoal))]"
                  >
                    {item.label}
                  </Link>
                  {item.columns && (
                    <button
                      onClick={() =>
                        setMobileExpanded(mobileExpanded === i ? null : i)
                      }
                      aria-label="Toggle submenu"
                      className="p-2 -mr-2"
                    >
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          mobileExpanded === i ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}
                </div>
                {item.columns && mobileExpanded === i && (
                  <div className="pb-3 pl-3">
                    {item.columns.map((col) => (
                      <div key={col.title} className="mt-2">
                        <div className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--rc-purple))] font-semibold mb-1">
                          {col.title}
                        </div>
                        <ul>
                          {col.items.map((sub) => (
                            <li key={sub.label}>
                              <Link
                                to={sub.href}
                                onClick={() => setMobileOpen(false)}
                                className="block py-2 text-[14px] text-[hsl(var(--rc-charcoal))]"
                              >
                                {sub.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href={`tel:${SITE.helplineTel}`}
              className="mt-4 inline-flex items-center gap-2 py-2 text-[hsl(var(--rc-deep))] font-semibold"
            >
              <Phone className="w-4 h-4" /> {SITE.helpline}
            </a>
            <Button
              asChild
              className="mt-2 mb-3 bg-[hsl(var(--rc-deep))] hover:bg-[hsl(var(--rc-purple))] text-white rounded-full"
            >
              <Link to="/donate" onClick={() => setMobileOpen(false)}>
                Donate
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
